// AUTO-GENERATED FILE - DO NOT EDIT
// Generated by scripts/generate-cube-meta.ts
import type {
  CubeMeasureEntry,
  CubeDimensionEntry,
  CubeJoinEntry
} from 'shared/src/api/cube-api-types';

export const campaignsCubeMeta = {
  name: 'campaigns',
  sqlTable: 'metrics.campaigns',
  title: 'Campaigns',
  description:
    "Campaign-level aggregates. One row per campaign. senior_media_trader is the single team-role senior trader, not the assignment roster. To group by the individually assigned media trader, group line_items measures by line_item_media_traders.media_trader_name (add campaigns.campaign_name for a per-campaign rollup); to filter to one trader, use a 'contains' filter on media_trader_ids. Never group by media_traders_json / media_trader_ids — they are roster arrays, so grouping buckets by roster, not by trader.",
  joins: {
    campaign_daily_metrics: { relationship: 'one_to_many' } as const satisfies CubeJoinEntry,
    zoho_line_items: { relationship: 'one_to_many' } as const satisfies CubeJoinEntry,
    notes: { relationship: 'one_to_many' } as const satisfies CubeJoinEntry,
    zohosign_documents: { relationship: 'one_to_many' } as const satisfies CubeJoinEntry,
    insertion_orders: { relationship: 'one_to_many' } as const satisfies CubeJoinEntry
  },
  measures: {
    'campaigns.total_line_item_price': {
      sql: 'total_line_item_price',
      type: 'number',
      aggType: 'sum',
      title: 'Total Line Item Price',
      format: 'currency',
      description: 'Sum of all line item prices for the campaign.',
      meta: {
        category: 'financial',
        grain: 'campaign',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(total_line_item_price)',
        inputs: [],
        short_description: 'Sum of all line item prices for the campaign',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.total_line_item_effective_price': {
      sql: 'campaigns.total_line_item_price * (1 - campaigns.referral_commission_rate)',
      type: 'number',
      aggType: 'sum',
      title: 'Total Line Item Effective Price',
      format: 'currency',
      description: 'Total line item price after subtracting referral commission.',
      meta: {
        category: 'financial',
        grain: 'campaign',
        type: 'effective',
        supports_rollup: true,
        formula: 'SUM(total_line_item_price * (1 - referral_commission_rate))',
        inputs: ['campaigns.total_line_item_price', 'campaigns.referral_commission_rate'],
        short_description: 'Total line item price after deducting referral commission',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.count': {
      type: 'number',
      aggType: 'count',
      title: 'Campaign Count',
      description: 'Number of campaigns.',
      meta: {
        category: 'utility',
        grain: 'campaign',
        supports_rollup: true,
        formula: 'COUNT(*)',
        inputs: [],
        short_description: 'Number of campaigns. For row counts, not business metrics',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.planned_margin': {
      sql: 'campaigns.overall_total_price_margin / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Planned Margin',
      format: 'percent',
      description:
        'Weighted average planned margin across line items. SUM(price * margin) / SUM(price).',
      meta: {
        category: 'margin',
        grain: 'campaign',
        type: 'planned',
        supports_rollup: false,
        formula: 'SUM(total_price_margin) / NULLIF(SUM(total_line_item_price), 0)',
        inputs: ['campaigns.total_price_margin', 'campaigns.total_line_item_price'],
        short_description: 'Weighted average planned margin across line items',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.target_margin': {
      sql: 'campaigns.overall_total_price_target_margin / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Target Margin',
      format: 'percent',
      description:
        'Weighted average target margin across line items. SUM(price * target_margin) / SUM(price).',
      meta: {
        category: 'margin',
        grain: 'campaign',
        type: 'target',
        supports_rollup: false,
        formula: 'SUM(total_price_target_margin) / NULLIF(SUM(total_line_item_price), 0)',
        inputs: ['campaigns.total_price_target_margin', 'campaigns.total_line_item_price'],
        short_description: 'Weighted average target margin across line items',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.uncapped_revenue': {
      sql: 'total_uncapped_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Uncapped Revenue',
      format: 'currency',
      description:
        'Total uncapped revenue (gross, before referral commission). Raw actual revenue without any cap applied. Use uncapped_effective_revenue for net-of-commission value.',
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'lifetime',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_uncapped_revenue)',
        inputs: [],
        short_description: 'Total uncapped revenue (gross, before referral commission)',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.uncapped_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.uncapped_effective_revenue': {
      sql: 'campaigns.total_uncapped_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Uncapped Effective Revenue',
      format: 'currency',
      description: 'Total uncapped revenue net of referral commission.',
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'lifetime',
        type: 'effective',
        supports_rollup: true,
        formula: 'SUM(total_uncapped_revenue * (1 - referral_commission_rate))',
        inputs: ['campaigns.uncapped_revenue_value', 'campaigns.referral_commission_rate'],
        short_description: 'Total uncapped revenue net of referral commission',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.uncapped_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.billable_revenue': {
      sql: 'total_billable_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Billable Revenue',
      format: 'currency',
      description:
        "Revenue billable to client across all line items. Applies monthly or lifetime cap per line item's rollover setting.",
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'lifetime',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_billable_revenue)',
        inputs: [],
        short_description: 'Revenue billable to client (capped per rollover setting)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.effective_billable_revenue': {
      sql: 'campaigns.total_billable_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Billable Revenue',
      format: 'currency',
      description: 'Billable revenue net of referral commission.',
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'lifetime',
        type: 'effective',
        supports_rollup: true,
        formula: 'total_billable_revenue * (1 - referral_commission_rate)',
        inputs: ['campaigns.billable_revenue', 'campaigns.referral_commission_rate'],
        short_description: 'Billable revenue net of referral commission',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.actual_spend': {
      sql: 'total_actual_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Actual Spend',
      format: 'currency',
      description: 'Total actual media spend.',
      meta: {
        category: 'spend',
        grain: 'campaign',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_actual_spend)',
        inputs: [],
        short_description: 'Total actual media spend',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.actual_spend'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.planned_spend': {
      sql: 'total_planned_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Spend',
      format: 'currency',
      description: 'Total planned media spend (budget).',
      meta: {
        category: 'spend',
        grain: 'campaign',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(total_planned_spend)',
        inputs: [],
        short_description: 'Total planned media spend (budget)',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.planned_spend'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.current_planned_spend': {
      sql: 'current_planned_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Latest Planned Spend',
      format: 'currency',
      description:
        'Planned spend through yesterday. Pre-aggregated from campaign_daily_metrics WHERE day <= yesterday. Represents what should have been spent by now according to pacing schedules. Denominator for spend_pacing. Updates daily.',
      meta: {
        category: 'spend',
        grain: 'campaign',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(current_planned_spend)',
        inputs: [],
        short_description: 'Planned spend through yesterday. Denominator for spend pacing',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.planned_spend'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.planned_revenue': {
      sql: 'current_planned_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Revenue',
      format: 'currency',
      description:
        'Cumulative planned revenue through yesterday. Pre-aggregated via MAX(cumulative_planned_revenue) WHERE day <= yesterday from campaign_daily_metrics. At the overall grain this SUMs across campaigns. Updates daily.',
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_revenue)',
        inputs: [],
        short_description: 'Cumulative planned revenue through yesterday',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.cumulative_planned_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.media_cost': {
      sql: 'total_media_cost',
      type: 'number',
      aggType: 'sum',
      title: 'Media Cost',
      format: 'currency',
      description:
        'Total media cost (COGS). This is the agency cost of goods sold, NOT the same as media spend. For standard line items, media_cost equals actual_spend. For management_fee line items, media_cost is always 0 because those represent agency services, not platform spend. Use actual_spend for total platform expenditure regardless of line item type.',
      meta: {
        category: 'spend',
        grain: 'campaign',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_media_cost)',
        inputs: [],
        short_description: 'Total media cost (COGS). 0 for management_fee line items',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.media_cost'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.lifetime_capped_actual_revenue': {
      sql: 'total_lifetime_capped_actual_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Lifetime Capped Revenue',
      format: 'currency',
      description: 'Lifetime capped revenue (bottom-up: LEAST per LI, summed to campaign).',
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'lifetime',
        type: 'capped_actual',
        supports_rollup: true,
        formula: 'SUM(total_lifetime_capped_actual_revenue)',
        inputs: [],
        short_description: 'Lifetime capped revenue (bottom-up: LEAST per LI, summed to campaign)',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.lifetime_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.effective_lifetime_capped_actual_revenue': {
      sql: 'campaigns.total_lifetime_capped_actual_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Lifetime Capped Revenue',
      format: 'currency',
      description: 'Lifetime capped revenue after deducting referral commission.',
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'lifetime',
        type: 'effective_capped_actual',
        supports_rollup: true,
        formula: 'SUM(total_lifetime_capped_actual_revenue * (1 - referral_commission_rate))',
        inputs: [
          'campaigns.lifetime_capped_actual_revenue_value',
          'campaigns.referral_commission_rate'
        ],
        short_description: 'Lifetime capped revenue after deducting referral commission',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.effective_lifetime_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.monthly_capped_actual_revenue': {
      sql: 'total_monthly_capped_actual_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Monthly Capped Revenue',
      format: 'currency',
      description:
        "Sum of per-month capped actual revenue across the flight. Each month's actual revenue is capped at that month's planned revenue total, then summed across all months.",
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'monthly',
        type: 'capped_actual',
        supports_rollup: true,
        formula: 'SUM(total_monthly_capped_actual_revenue)',
        inputs: [],
        short_description: 'Sum of per-month capped actual revenue across the flight',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.monthly_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.effective_monthly_capped_actual_revenue': {
      sql: 'campaigns.total_monthly_capped_actual_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Monthly Capped Revenue',
      format: 'currency',
      description: 'Monthly capped revenue after deducting referral commission.',
      meta: {
        category: 'revenue',
        grain: 'campaign',
        period: 'monthly',
        type: 'effective_capped_actual',
        supports_rollup: true,
        formula: 'SUM(total_monthly_capped_actual_revenue * (1 - referral_commission_rate))',
        inputs: [
          'campaigns.monthly_capped_actual_revenue_value',
          'campaigns.referral_commission_rate'
        ],
        short_description: 'Monthly capped revenue after deducting referral commission',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.effective_monthly_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.actual_margin_dollars': {
      sql: 'campaigns.effective_billable_revenue - campaigns.media_cost',
      type: 'number',
      aggType: 'number',
      title: 'Actual Margin $',
      format: 'currency',
      description: 'Actual margin in dollars. Effective billable revenue minus COGS.',
      meta: {
        category: 'margin',
        grain: 'campaign',
        type: 'actual',
        supports_rollup: false,
        formula: 'effective_billable_revenue - media_cost',
        inputs: ['campaigns.effective_billable_revenue', 'campaigns.media_cost'],
        short_description: 'Actual margin in dollars (effective billable revenue minus COGS)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.actual_margin_percent': {
      sql: '(campaigns.effective_billable_revenue - campaigns.media_cost) / NULLIF(campaigns.effective_billable_revenue, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Actual Margin %',
      format: 'percent',
      description: 'Actual margin as percentage of effective billable revenue.',
      meta: {
        category: 'margin',
        grain: 'campaign',
        type: 'actual',
        supports_rollup: false,
        formula:
          '(effective_billable_revenue - media_cost) / NULLIF(effective_billable_revenue, 0)',
        inputs: ['campaigns.effective_billable_revenue', 'campaigns.media_cost'],
        short_description: 'Actual margin as percentage of effective billable revenue',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.spend_pacing': {
      sql: 'campaigns.actual_spend / NULLIF(campaigns.current_planned_spend, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Current Spend %',
      format: 'percent',
      description:
        'Spend pacing ratio. Actual spend / planned spend through yesterday. A value of 1.0 means on pace, >1.0 means overspending, <1.0 means underspending. NON-ADDITIVE: cannot be summed across campaigns; use overall_spend_pacing for cross-campaign aggregation.',
      meta: {
        category: 'pacing',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_spend / NULLIF(current_planned_spend, 0)',
        inputs: ['campaigns.actual_spend', 'campaigns.current_planned_spend'],
        short_description:
          'Spend pacing ratio. 1.0 = on pace. Use overall_spend_pacing for cross-campaign',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.on_pace_effective_revenue': {
      sql: 'campaigns.current_planned_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'On-Pace Effective Revenue',
      format: 'currency',
      description:
        'Time-proportional planned revenue net of referral commission. Cumulative expected effective revenue through yesterday based on flight schedule.',
      meta: {
        category: 'pacing',
        grain: 'campaign',
        type: 'effective',
        supports_rollup: true,
        formula: 'SUM(current_planned_revenue * (1 - referral_commission_rate))',
        inputs: ['campaigns.planned_revenue_value', 'campaigns.referral_commission_rate'],
        short_description: 'Time-proportional planned revenue net of referral commission',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.cumulative_planned_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.delivery_pacing': {
      sql: 'campaigns.uncapped_revenue / NULLIF(campaigns.planned_revenue, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Current Pacing %',
      format: 'percent',
      description:
        'Delivery pacing ratio. Uncapped revenue / on-pace planned revenue. A value of 1.0 means on pace. NON-ADDITIVE: cannot be summed across campaigns; use overall_delivery_pacing for cross-campaign aggregation.',
      meta: {
        category: 'pacing',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: 'effective_billable_revenue / NULLIF(on_pace_effective_revenue, 0)',
        inputs: ['campaigns.effective_billable_revenue', 'campaigns.on_pace_effective_revenue'],
        short_description:
          'Delivery pacing ratio. 1.0 = on pace. Use overall_delivery_pacing for cross-campaign',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.revenue_delivered_percent': {
      sql: 'campaigns.billable_revenue / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Revenue Delivered %',
      format: 'percent',
      description:
        'Percentage of campaign billable revenue delivered. Uses gross price (before commission).',
      meta: {
        category: 'pacing',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: 'effective_billable_revenue / NULLIF(total_line_item_effective_price, 0)',
        inputs: [
          'campaigns.effective_billable_revenue',
          'campaigns.total_line_item_effective_price'
        ],
        short_description: 'Percentage of campaign effective billable revenue delivered',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.margin_variance': {
      sql: '(campaigns.actual_margin_percent - campaigns.planned_margin) / NULLIF(campaigns.planned_margin, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Margin Variance',
      format: 'percent',
      description: 'Variance between actual and planned margin.',
      meta: {
        category: 'margin',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: '(actual_margin_percent - planned_margin) / NULLIF(planned_margin, 0)',
        inputs: ['campaigns.actual_margin_percent', 'campaigns.planned_margin'],
        short_description: 'Variance between actual and planned margin',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.target_margin_variance': {
      sql: '(campaigns.target_margin - campaigns.planned_margin) / NULLIF(campaigns.target_margin, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Margin Discount',
      format: 'percent',
      description: 'Variance between target and planned margin.',
      meta: {
        category: 'margin',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: '(target_margin - planned_margin) / NULLIF(target_margin, 0)',
        inputs: ['campaigns.target_margin', 'campaigns.planned_margin'],
        short_description: 'Variance between target and planned margin (discount)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.percent_budget_spend': {
      sql: 'campaigns.actual_spend / NULLIF(campaigns.planned_spend, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Budget Spend %',
      format: 'percent',
      description: 'Percentage of total budget spent.',
      meta: {
        category: 'pacing',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_spend / NULLIF(planned_spend, 0)',
        inputs: ['campaigns.actual_spend', 'campaigns.planned_spend'],
        short_description: 'Percentage of total budget spent',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.budget_remaining': {
      sql: 'campaigns.planned_spend - campaigns.actual_spend',
      type: 'number',
      aggType: 'number',
      title: 'Budget Remaining',
      format: 'currency',
      description: 'Budget left to spend.',
      meta: {
        category: 'financial',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: 'planned_spend - actual_spend',
        inputs: ['campaigns.planned_spend', 'campaigns.actual_spend'],
        short_description: 'Budget left to spend',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.revenue_remaining': {
      sql: 'campaigns.total_line_item_price - campaigns.billable_revenue',
      type: 'number',
      aggType: 'number',
      title: 'Revenue Remaining',
      format: 'currency',
      description: 'Billable revenue left to earn.',
      meta: {
        category: 'financial',
        grain: 'campaign',
        type: 'derived',
        supports_rollup: false,
        formula: 'total_line_item_effective_price - effective_billable_revenue',
        inputs: [
          'campaigns.total_line_item_effective_price',
          'campaigns.effective_billable_revenue'
        ],
        short_description: 'Effective billable revenue left to earn',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.campaigns_count': {
      type: 'number',
      aggType: 'count',
      title: 'Campaigns Count',
      description: 'Count of campaigns. Replaces countDistinct from campaign_daily_metrics.',
      meta: {
        category: 'utility',
        grain: 'campaign',
        supports_rollup: true,
        formula: 'COUNT(*)',
        inputs: [],
        short_description: 'Count of campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_price': {
      sql: 'campaigns.price',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Price',
      format: 'currency',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(price)',
        inputs: ['campaigns.price'],
        short_description: 'Sum of campaign prices across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_effective_price': {
      sql: 'campaigns.price * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Effective Price',
      format: 'currency',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'effective',
        supports_rollup: true,
        formula: 'SUM(price * (1 - referral_commission_rate))',
        inputs: ['campaigns.price', 'campaigns.referral_commission_rate'],
        short_description: 'Sum of effective campaign prices (net of commission)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_total_price_margin': {
      sql: 'campaigns.total_price_margin',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Total Price Margin',
      format: 'currency',
      meta: {
        category: 'overall',
        grain: 'overall',
        supports_rollup: true,
        formula: 'SUM(total_price_margin)',
        inputs: ['campaigns.total_price_margin'],
        short_description: 'Aggregate numerator for weighted planned margin calculation',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_total_price_target_margin': {
      sql: 'campaigns.total_price_target_margin',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Total Price Target Margin',
      format: 'currency',
      meta: {
        category: 'overall',
        grain: 'overall',
        supports_rollup: true,
        formula: 'SUM(total_price_target_margin)',
        inputs: ['campaigns.total_price_target_margin'],
        short_description: 'Aggregate numerator for weighted target margin calculation',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_target_margin_price': {
      sql: 'campaigns.target_margin_price',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Target Margin Price',
      format: 'currency',
      meta: {
        category: 'overall',
        grain: 'overall',
        supports_rollup: true,
        formula: 'SUM(target_margin_price)',
        inputs: ['campaigns.target_margin_price'],
        short_description: 'Aggregate denominator for weighted target margin calculation',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_planned_margin': {
      sql: 'campaigns.overall_total_price_margin / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Planned Margin',
      format: 'percent',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'planned',
        supports_rollup: false,
        formula: 'SUM(total_price_margin) / NULLIF(SUM(total_line_item_price), 0)',
        inputs: ['campaigns.total_price_margin', 'campaigns.total_line_item_price'],
        short_description: 'Weighted average planned margin across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_target_margin': {
      sql: 'campaigns.overall_total_price_target_margin / NULLIF(campaigns.overall_target_margin_price, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Target Margin',
      format: 'percent',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'target',
        supports_rollup: false,
        formula: 'SUM(total_price_target_margin) / NULLIF(SUM(target_margin_price), 0)',
        inputs: ['campaigns.total_price_target_margin', 'campaigns.target_margin_price'],
        short_description: 'Weighted average target margin across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_target_margin_variance': {
      sql: '(campaigns.overall_target_margin - campaigns.overall_planned_margin) / NULLIF(campaigns.overall_target_margin, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Target Margin Variance',
      format: 'percent',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula:
          '(overall_target_margin - overall_planned_margin) / NULLIF(overall_target_margin, 0)',
        inputs: ['campaigns.overall_target_margin', 'campaigns.overall_planned_margin'],
        short_description: 'Variance between overall target and planned margin',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.earliest_flight_date': {
      sql: 'campaigns.flight_date',
      type: 'number',
      aggType: 'min',
      title: 'Earliest Flight Date',
      meta: {
        category: 'utility',
        grain: 'overall',
        supports_rollup: true,
        formula: 'MIN(flight_date)',
        inputs: ['campaigns.flight_date'],
        short_description: 'Earliest flight date across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.latest_end_date': {
      sql: 'campaigns.end_date',
      type: 'number',
      aggType: 'max',
      title: 'Latest End Date',
      meta: {
        category: 'utility',
        grain: 'overall',
        supports_rollup: true,
        formula: 'MAX(end_date)',
        inputs: ['campaigns.end_date'],
        short_description: 'Latest end date across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_uncapped_revenue': {
      sql: 'total_uncapped_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Uncapped Revenue',
      format: 'currency',
      description: 'Sum of gross uncapped revenue across all filtered campaigns.',
      meta: {
        category: 'overall',
        supports_rollup: false,
        grain: 'overall',
        period: 'lifetime',
        type: 'actual',
        formula: 'SUM(total_uncapped_revenue)',
        inputs: ['campaigns.uncapped_revenue'],
        short_description: 'Unfiltered total uncapped revenue across all campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_uncapped_effective_revenue': {
      sql: 'campaigns.total_uncapped_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Uncapped Effective Revenue',
      format: 'currency',
      description:
        'Sum of uncapped effective revenue (net of commission) across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        period: 'lifetime',
        type: 'effective',
        supports_rollup: false,
        formula: 'SUM(total_uncapped_revenue * (1 - referral_commission_rate))',
        inputs: ['campaigns.uncapped_revenue', 'campaigns.referral_commission_rate'],
        short_description: 'Unfiltered total uncapped revenue net of commission',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_billable_revenue': {
      sql: 'total_billable_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Billable Revenue',
      format: 'currency',
      meta: {
        category: 'overall',
        grain: 'overall',
        period: 'lifetime',
        type: 'actual',
        supports_rollup: false,
        formula: 'SUM(total_billable_revenue)',
        inputs: ['campaigns.billable_revenue'],
        short_description: 'Unfiltered total billable revenue across all campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_effective_billable_revenue': {
      sql: 'campaigns.total_billable_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Effective Billable Revenue',
      format: 'currency',
      meta: {
        category: 'overall',
        grain: 'overall',
        period: 'lifetime',
        type: 'effective',
        supports_rollup: false,
        formula: 'SUM(total_billable_revenue * (1 - referral_commission_rate))',
        inputs: ['campaigns.billable_revenue', 'campaigns.referral_commission_rate'],
        short_description: 'Unfiltered total billable revenue net of commission',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_spend': {
      sql: 'total_actual_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Media Spend',
      format: 'currency',
      description: 'Sum of actual spend across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_actual_spend)',
        inputs: ['campaigns.actual_spend_value'],
        short_description: 'Sum of actual spend across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_budget': {
      sql: 'total_planned_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Media Budget',
      format: 'currency',
      description: 'Sum of planned spend across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(total_planned_spend)',
        inputs: ['campaigns.planned_spend_value'],
        short_description: 'Sum of planned spend across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_effective_on_pace_revenue': {
      sql: 'campaigns.current_planned_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Effective On-Pace Revenue',
      format: 'currency',
      description:
        'Sum of time-proportional planned revenue net of commission across all filtered campaigns. Cumulative expected effective revenue through yesterday.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'effective',
        supports_rollup: true,
        formula: 'SUM(current_planned_revenue * (1 - referral_commission_rate))',
        inputs: ['campaigns.planned_revenue_value', 'campaigns.referral_commission_rate'],
        short_description: 'Sum of time-proportional planned revenue net of commission',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_on_pace_spend': {
      sql: 'current_planned_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Overall On-Pace Spend',
      format: 'currency',
      description: 'Sum of planned spend through yesterday across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(current_planned_spend)',
        inputs: ['campaigns.current_planned_spend_value'],
        short_description: 'Sum of planned spend through yesterday',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_media_cost': {
      sql: 'total_media_cost',
      type: 'number',
      aggType: 'sum',
      title: 'Overall Media Cost',
      format: 'currency',
      description: 'Sum of media cost (COGS) across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_media_cost)',
        inputs: ['campaigns.media_cost_value'],
        short_description: 'Sum of media cost (COGS) across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_delivery_pacing': {
      sql: 'campaigns.overall_billable_revenue / NULLIF(campaigns.overall_on_pace_planned_revenue, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Delivery Pacing',
      format: 'percent',
      description:
        'Weighted average delivery pacing across all filtered campaigns. Billable revenue / on-pace planned revenue.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula:
          'overall_effective_billable_revenue / NULLIF(overall_effective_on_pace_revenue, 0)',
        inputs: [
          'campaigns.overall_effective_billable_revenue',
          'campaigns.overall_effective_on_pace_revenue'
        ],
        short_description: 'Weighted average delivery pacing across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_on_pace_planned_revenue': {
      sql: 'current_planned_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Overall On-Pace Planned Revenue',
      format: 'currency',
      description: 'Sum of planned revenue through yesterday. Component for delivery pacing.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_revenue)',
        inputs: ['campaigns.planned_revenue_value'],
        short_description:
          'Sum of planned revenue through yesterday. Component for delivery pacing',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_spend_pacing': {
      sql: 'campaigns.overall_spend / NULLIF(campaigns.overall_on_pace_spend, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Spend Pacing',
      format: 'percent',
      description: 'Weighted average spend pacing across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula: 'overall_spend / NULLIF(overall_on_pace_spend, 0)',
        inputs: ['campaigns.overall_spend', 'campaigns.overall_on_pace_spend'],
        short_description: 'Weighted average spend pacing across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_percent_budget_spend': {
      sql: 'campaigns.overall_spend / NULLIF(campaigns.overall_budget, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall % Budget Spent',
      format: 'percent',
      description: 'Percentage of total budget spent across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula: 'overall_spend / NULLIF(overall_budget, 0)',
        inputs: ['campaigns.overall_spend', 'campaigns.overall_budget'],
        short_description: 'Percentage of total budget spent across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_margin_actual': {
      sql: '(campaigns.overall_effective_billable_revenue - campaigns.overall_media_cost) / NULLIF(campaigns.overall_effective_billable_revenue, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Actual Margin',
      format: 'percent',
      description: 'Actual margin percentage across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'actual',
        supports_rollup: false,
        formula:
          '(overall_effective_billable_revenue - overall_media_cost) / NULLIF(overall_effective_billable_revenue, 0)',
        inputs: ['campaigns.overall_effective_billable_revenue', 'campaigns.overall_media_cost'],
        short_description: 'Actual margin percentage across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_budget_remaining': {
      sql: 'campaigns.overall_budget - campaigns.overall_spend',
      type: 'number',
      aggType: 'number',
      title: 'Overall Budget Remaining',
      format: 'currency',
      description: 'Total remaining budget across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula: 'overall_budget - overall_spend',
        inputs: ['campaigns.overall_budget', 'campaigns.overall_spend'],
        short_description: 'Total remaining budget across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_percent_revenue_delivered': {
      sql: 'campaigns.overall_billable_revenue / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Revenue Delivered %',
      format: 'percent',
      description: 'Percentage of total billable revenue delivered across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula: 'overall_effective_billable_revenue / NULLIF(total_line_item_effective_price, 0)',
        inputs: [
          'campaigns.overall_effective_billable_revenue',
          'campaigns.total_line_item_effective_price'
        ],
        short_description: 'Percentage of total effective billable revenue delivered',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_margin_variance': {
      sql: '(campaigns.overall_margin_actual - campaigns.overall_planned_margin) / NULLIF(campaigns.overall_planned_margin, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Overall Margin Variance',
      format: 'percent',
      description: 'Variance between actual and planned margin across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula:
          '(overall_margin_actual - overall_planned_margin) / NULLIF(overall_planned_margin, 0)',
        inputs: ['campaigns.overall_margin_actual', 'campaigns.overall_planned_margin'],
        short_description:
          'Variance between actual and planned margin across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry,
    'campaigns.overall_revenue_remaining': {
      sql: 'campaigns.total_line_item_price - campaigns.overall_billable_revenue',
      type: 'number',
      aggType: 'number',
      title: 'Overall Revenue Remaining',
      format: 'currency',
      description: 'Billable revenue left to earn across all filtered campaigns.',
      meta: {
        category: 'overall',
        grain: 'overall',
        type: 'derived',
        supports_rollup: false,
        formula: 'total_line_item_effective_price - overall_effective_billable_revenue',
        inputs: [
          'campaigns.total_line_item_effective_price',
          'campaigns.overall_effective_billable_revenue'
        ],
        short_description: 'Effective billable revenue left to earn across all filtered campaigns',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeMeasureEntry
  },
  dimensions: {
    'campaigns.id': {
      sql: 'id',
      type: 'string',
      title: 'Id',
      primaryKey: true,
      meta: {
        short_description: 'Unique campaign identifier',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.campaign_name': {
      sql: 'campaign_name',
      type: 'string',
      title: 'Campaign Name',
      meta: {
        short_description: 'Campaign display name',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.campaign_number': {
      sql: 'campaign_number',
      type: 'string',
      title: 'Campaign Number',
      meta: {
        short_description: 'Human-readable campaign number (e.g., BRKC-1234)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.stage': {
      sql: 'stage',
      type: 'string',
      title: 'Stage',
      meta: {
        short_description:
          'Campaign lifecycle stage. Valid values: Potential Opportunity, New, Media Campaign Details, Avails/Plan Development, Proposal Sent, Book the Campaign, Campaign Approved to Launch, Traffic & Onboard, Pixel Setup, Pixel Check, Campaign is Live, Campaign is Paused, Campaign Management & Reporting, Campaign Complete, Renew Campaign, Closed (Lost), Campaign Cancelled, Exit - Dev Only, Potential',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.stage_sort_order': {
      sql: 'stage_sort_order',
      type: 'number',
      title: 'Stage Sort Order',
      description:
        'Numeric sort order for stage: 1-3 active, 4-14 pipeline, 15-19 completed/cancelled',
      meta: {
        short_description:
          'Numeric sort order for stage: 1-3 active, 4-14 pipeline, 15-19 completed/cancelled',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.campaign_source': {
      sql: 'campaign_source',
      type: 'string',
      title: 'Campaign Source',
      meta: {
        short_description: 'Lead source of the campaign (e.g., Inbound, Referral)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.new_business': {
      sql: 'new_business',
      type: 'boolean',
      title: 'New Business',
      meta: {
        short_description:
          'Whether this campaign represents new business (true) or renewal/existing (false)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.goals_kpis': {
      sql: 'goals_kpis',
      type: 'string',
      title: 'Goals/KPIs',
      meta: {
        short_description: 'Campaign goals and KPIs text field',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.account_id': {
      sql: 'account_id',
      type: 'string',
      title: 'Account ID',
      meta: {
        short_description: 'Parent account identifier. Group campaigns by account',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.account_name': {
      sql: 'account_name',
      type: 'string',
      title: 'Account Name',
      meta: {
        short_description: 'Parent account display name',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.account_type': {
      sql: 'account_type',
      type: 'string',
      title: 'Account Type',
      meta: {
        short_description: 'Account type classification (e.g., Direct, Agency)',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.account_industry': {
      sql: 'account_industry',
      type: 'string',
      title: 'Industry',
      meta: {
        short_description: 'Industry vertical of the parent account',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.account_region': {
      sql: 'account_region',
      type: 'string',
      title: 'Region',
      meta: {
        short_description: 'Geographic region of the parent account',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.agency_id': {
      sql: 'agency_id',
      type: 'string',
      title: 'Agency ID',
      meta: {
        short_description: 'Agency identifier, if campaign is agency-managed',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.agency_name': {
      sql: 'agency_name',
      type: 'string',
      title: 'Agency Name',
      meta: {
        short_description: 'Agency display name, if campaign is agency-managed',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.team_id': {
      sql: 'team_id',
      type: 'string',
      title: 'Team ID',
      meta: {
        short_description: 'Assigned team identifier',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.account_team_name': {
      sql: 'account_team_name',
      type: 'string',
      title: 'Team',
      meta: {
        short_description: 'Assigned team name. Filter or group by team',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.account_manager': {
      sql: 'account_manager',
      type: 'string',
      title: 'Account Manager',
      meta: {
        short_description: 'Assigned account manager name',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.senior_media_trader': {
      sql: 'senior_media_trader',
      type: 'string',
      title: 'Senior Media Trader',
      meta: {
        short_description: 'Assigned senior media trader name',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.client_success_director': {
      sql: 'client_success_director',
      type: 'string',
      title: 'Client Success Director',
      meta: {
        short_description: 'Assigned client success director name',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.media_traders_json': {
      sql: 'media_traders_json',
      type: 'string',
      title: 'Media Traders',
      description:
        'JSON array of media trader user objects (id, name, email, zoho_user_id). Display/filter only; to group by trader use line_item_media_traders.media_trader_name.',
      meta: {
        filter_only: true,
        short_description: 'Assigned media traders as JSON objects — display/filter only',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.media_trader_ids': {
      sql: 'campaigns.media_trader_ids::text',
      type: 'string',
      title: 'Media Trader IDs',
      description:
        "Campaign's trader roster as uuid[]::text, unioned from its line items. Filter only — use 'contains' with a trader UUID. To group by trader use line_item_media_traders.media_trader_name.",
      meta: {
        filter_only: true,
        short_description: "Campaign trader roster user IDs — 'contains'-filter by UUID only",
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.lead_account_owner': {
      sql: 'lead_account_owner',
      type: 'string',
      title: 'Lead Account Owner',
      meta: {
        short_description: 'Lead account owner name from CRM',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.flight_date': {
      sql: 'flight_date',
      type: 'time',
      title: 'Flight Date',
      meta: {
        short_description: 'Campaign flight start date',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.end_date': {
      sql: 'end_date',
      type: 'time',
      title: 'End Date',
      meta: {
        short_description: 'Campaign end date',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.budget': {
      sql: 'budget',
      type: 'number',
      title: 'Budget',
      format: 'currency',
      meta: {
        category: 'financial',
        short_description: 'Campaign budget amount from CRM',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.is_budget_different': {
      sql: '(campaigns.budget <> campaigns.total_line_item_price)',
      type: 'boolean',
      title: 'Is Budget Different',
      meta: {
        category: 'financial',
        short_description: 'Is the budget from zoho different from the total line item price',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.is_budget_different_from_effective': {
      sql: '(campaigns.budget <> campaigns.total_line_item_price * (1 - COALESCE(campaigns.referral_commission_rate, 0)))',
      type: 'boolean',
      title: 'Budget Differs From Effective Price',
      meta: {
        category: 'financial',
        short_description:
          'Budget differs from effective line item price (after referral deduction). True discrepancy flag.',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.budget_line_item_difference': {
      sql: 'campaigns.budget - campaigns.total_line_item_price',
      type: 'number',
      title: 'Budget vs Line Item Price Difference',
      format: 'currency',
      meta: {
        category: 'financial',
        short_description:
          'Campaign budget minus total line item price. Positive = budget exceeds price.',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.budget_effective_difference': {
      sql: 'campaigns.budget - campaigns.total_line_item_price * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      title: 'Budget vs Effective Price Difference',
      format: 'currency',
      meta: {
        category: 'financial',
        short_description:
          'Campaign budget minus effective line item price. Non-zero indicates a true discrepancy.',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.budget_strategy_difference': {
      sql: 'campaigns.budget - campaigns.price',
      type: 'number',
      title: 'Budget vs Strategy Price Difference',
      format: 'currency',
      meta: {
        category: 'financial',
        short_description:
          'Zoho budget minus Bravo strategy price. Non-zero indicates a discrepancy.',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.budget_discrepancy_type': {
      sql: "CASE WHEN campaigns.total_line_item_price = 0 THEN 'no_line_items' WHEN campaigns.budget = campaigns.total_line_item_price THEN 'synced' WHEN COALESCE(campaigns.referral_commission_rate, 0) > 0 AND campaigns.budget = campaigns.total_line_item_price * (1 - campaigns.referral_commission_rate) THEN 'referral_adjusted' ELSE 'true_mismatch' END",
      type: 'string',
      title: 'Budget Discrepancy Type',
      meta: {
        category: 'financial',
        short_description:
          'Classifies budget discrepancy: synced, no_line_items, referral_adjusted, or true_mismatch',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.proposed_budget': {
      sql: 'proposed_budget',
      type: 'number',
      title: 'Proposed Budget',
      format: 'currency',
      meta: {
        category: 'financial',
        short_description: 'Originally proposed campaign budget',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.expected_revenue': {
      sql: 'expected_revenue',
      type: 'number',
      title: 'Expected Revenue',
      format: 'currency',
      meta: {
        category: 'financial',
        short_description: 'Expected revenue from CRM pipeline',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.agency_gross_markup': {
      sql: 'agency_gross_markup',
      type: 'number',
      title: 'Agency Gross Markup',
      format: 'percent',
      meta: {
        category: 'financial',
        short_description: 'Agency gross markup percentage applied to media cost',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.referral_commission_rate': {
      sql: 'referral_commission_rate',
      type: 'number',
      title: 'Referral Commission Rate',
      format: 'percent',
      meta: {
        category: 'commission',
        short_description:
          'Referral commission rate deducted from gross revenue to get effective revenue',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.price': {
      sql: 'price',
      type: 'number',
      title: 'Price',
      format: 'currency',
      meta: {
        category: 'financial',
        short_description:
          'Strategy-level campaign price from Bravo, gross, before referral commission',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.effective_price': {
      sql: 'campaigns.price * (1 - campaigns.referral_commission_rate)',
      type: 'number',
      title: 'Effective Price',
      format: 'currency',
      meta: {
        category: 'financial',
        formula: 'price * (1 - referral_commission_rate)',
        inputs: ['campaigns.price', 'campaigns.referral_commission_rate'],
        short_description: 'Campaign price after deducting referral commission. Net revenue target',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.line_items_count': {
      sql: 'line_items_count',
      type: 'number',
      title: 'Line Items Count',
      meta: {
        short_description: 'Total number of line items in this campaign',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.line_items_with_platforms_count': {
      sql: 'line_items_with_platforms_count',
      type: 'number',
      title: 'Line Items With Platforms',
      meta: {
        short_description: 'Number of line items that have platform assignments',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.line_items_with_media_plans_count': {
      sql: 'line_items_with_media_plans_count',
      type: 'number',
      title: 'Line Items With Media Plans',
      meta: {
        short_description: 'Number of line items that have media plans',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.total_media_plans_count': {
      sql: 'total_media_plans_count',
      type: 'number',
      title: 'Total Media Plans Count',
      description: 'Total number of media plans across all line items',
      meta: {
        short_description: 'Total number of media plans across all line items',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.active_media_plans_count': {
      sql: 'active_media_plans_count',
      type: 'number',
      title: 'Active Media Plans Count',
      description: 'Number of media plans with active platform buys',
      meta: {
        short_description: 'Number of media plans with active platform buys',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.target_margin_price': {
      sql: 'target_margin_price',
      type: 'number',
      title: 'Target Margin Price',
      format: 'currency',
      description:
        'Sum of line item prices excluding management_fee and zero_dollar types. Used as denominator for weighted margin calculations.',
      meta: {
        category: 'margin',
        short_description:
          'Sum of line item prices excluding management_fee and zero_dollar types. Denominator for weighted margin',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.total_price_margin': {
      sql: 'total_price_margin',
      type: 'number',
      title: 'Total Price Margin',
      format: 'currency',
      description:
        'Sum of (price * planned_margin) for line items excluding management_fee and zero_dollar. Numerator for weighted planned margin.',
      meta: {
        category: 'margin',
        short_description:
          'Sum of (price * planned_margin) excluding management_fee and zero_dollar. Numerator for weighted planned margin',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.total_price_target_margin': {
      sql: 'total_price_target_margin',
      type: 'number',
      title: 'Total Price Target Margin',
      format: 'currency',
      description:
        'Sum of (price * target_margin) for line items excluding management_fee and zero_dollar. Numerator for weighted target margin.',
      meta: {
        category: 'margin',
        short_description:
          'Sum of (price * target_margin) excluding management_fee and zero_dollar. Numerator for weighted target margin',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.uncapped_revenue_value': {
      sql: 'total_uncapped_revenue',
      type: 'number',
      title: 'Total Uncapped Revenue (Raw)',
      format: 'currency',
      description:
        'Pre-aggregated SUM(uncapped_revenue) from campaign_daily_metrics. Raw value before commission adjustment.',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.uncapped_revenue',
        category: 'revenue',
        type: 'actual',
        short_description: 'Filter-only: per-row uncapped revenue (before commission)',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.uncapped_revenue'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.billable_revenue_value': {
      sql: 'total_billable_revenue',
      type: 'number',
      title: 'Billable Revenue (filter)',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.billable_revenue',
        short_description: 'Filter-only dimension for billable revenue',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.effective_billable_revenue_value': {
      sql: 'campaigns.total_billable_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      title: 'Effective Billable Revenue (filter)',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.effective_billable_revenue',
        short_description: 'Filter-only dimension for effective billable revenue',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.actual_spend_value': {
      sql: 'total_actual_spend',
      type: 'number',
      title: 'Total Actual Spend (Raw)',
      format: 'currency',
      description: 'Pre-aggregated SUM(actual_spend) from campaign_daily_metrics.',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.actual_spend',
        category: 'spend',
        type: 'actual',
        short_description: 'Filter-only: per-row actual spend',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.actual_spend'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.planned_spend_value': {
      sql: 'total_planned_spend',
      type: 'number',
      title: 'Total Planned Spend (Raw)',
      format: 'currency',
      description: 'Pre-aggregated SUM(planned_spend) from campaign_daily_metrics.',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.planned_spend',
        category: 'spend',
        type: 'planned',
        short_description: 'Filter-only: per-row planned spend',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.planned_spend'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.media_cost_value': {
      sql: 'total_media_cost',
      type: 'number',
      title: 'Total Media Cost (Raw)',
      format: 'currency',
      description: 'Pre-aggregated SUM(media_cost) from campaign_daily_metrics.',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.media_cost',
        category: 'spend',
        type: 'actual',
        short_description: 'Filter-only: per-row media cost',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.media_cost'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.current_planned_spend_value': {
      sql: 'current_planned_spend',
      type: 'number',
      title: 'Current Planned Spend (Raw)',
      format: 'currency',
      description:
        'Pre-aggregated SUM(planned_spend) WHERE day <= yesterday from campaign_daily_metrics.',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.current_planned_spend',
        category: 'spend',
        type: 'planned',
        short_description: 'Filter-only: per-row planned spend through yesterday',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.planned_spend'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.planned_revenue_value': {
      sql: 'current_planned_revenue',
      type: 'number',
      title: 'Planned Revenue (Raw)',
      format: 'currency',
      description:
        'Pre-aggregated MAX(cumulative_planned_revenue) WHERE day <= yesterday from campaign_daily_metrics.',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.planned_revenue',
        category: 'revenue',
        type: 'planned',
        short_description: 'Filter-only: per-row planned revenue through yesterday',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.cumulative_planned_revenue'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.lifetime_capped_actual_revenue_value': {
      sql: 'total_lifetime_capped_actual_revenue',
      type: 'number',
      title: 'Total Lifetime Capped Revenue (Raw)',
      format: 'currency',
      description:
        'Pre-aggregated MAX(lifetime_capped_actual_revenue) WHERE day <= yesterday from campaign_daily_metrics.',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.lifetime_capped_actual_revenue',
        category: 'revenue',
        type: 'capped_actual',
        short_description: 'Filter-only: per-row lifetime capped actual revenue',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.lifetime_capped_actual_revenue'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.monthly_capped_actual_revenue_value': {
      sql: 'total_monthly_capped_actual_revenue',
      type: 'number',
      title: 'Total Monthly Capped Revenue (Raw)',
      format: 'currency',
      description:
        "Pre-aggregated SUM of per-month capped actual revenue. Each month's actual is capped at that month's planned total, then summed across all months.",
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.monthly_capped_actual_revenue',
        category: 'revenue',
        type: 'capped_actual',
        short_description: 'Filter-only: per-row monthly capped actual revenue',
        source_tables: ['metrics.campaigns'],
        rolled_up_from: 'campaign_daily_metrics.monthly_capped_actual_revenue'
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.line_item_price_value': {
      sql: 'total_line_item_price',
      type: 'number',
      title: 'Line Item Price Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.total_line_item_price',
        short_description: 'Filter-only: per-row total line item price',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.line_item_effective_price_value': {
      sql: 'campaigns.total_line_item_price * (1 - campaigns.referral_commission_rate)',
      type: 'number',
      title: 'Line Item Effective Price Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.total_line_item_effective_price',
        short_description: 'Filter-only: per-row total line item effective price',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.effective_monthly_capped_actual_revenue_value': {
      sql: 'campaigns.total_monthly_capped_actual_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      title: 'Effective Monthly Capped Actual Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.effective_monthly_capped_actual_revenue',
        short_description: 'Filter-only: per-row effective monthly capped revenue',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.effective_lifetime_capped_actual_revenue_value': {
      sql: 'campaigns.total_lifetime_capped_actual_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0))',
      type: 'number',
      title: 'Effective Lifetime Capped Actual Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.effective_lifetime_capped_actual_revenue',
        short_description: 'Filter-only: per-row effective lifetime capped revenue',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.budget_remaining_value': {
      sql: 'campaigns.total_planned_spend - campaigns.total_actual_spend',
      type: 'number',
      title: 'Budget Remaining Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.budget_remaining',
        short_description: 'Filter-only: per-row budget remaining',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.revenue_remaining_value': {
      sql: 'campaigns.total_line_item_price - campaigns.total_billable_revenue',
      type: 'number',
      title: 'Revenue Remaining Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.revenue_remaining',
        short_description: 'Filter-only: per-row revenue remaining',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.delivery_pacing_value': {
      sql: 'campaigns.total_billable_revenue / NULLIF(campaigns.current_planned_revenue, 0)',
      type: 'number',
      title: 'Delivery Pacing Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.delivery_pacing',
        short_description: 'Filter-only: per-row delivery pacing',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.spend_pacing_value': {
      sql: 'campaigns.total_actual_spend / NULLIF(campaigns.current_planned_spend, 0)',
      type: 'number',
      title: 'Spend Pacing Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.spend_pacing',
        short_description: 'Filter-only: per-row spend pacing',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.percent_budget_spend_value': {
      sql: 'campaigns.total_actual_spend / NULLIF(campaigns.total_planned_spend, 0)',
      type: 'number',
      title: 'Percent Budget Spend Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.percent_budget_spend',
        short_description: 'Filter-only: per-row percent budget spent',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.actual_margin_percent_value': {
      sql: '(campaigns.total_billable_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0)) - campaigns.total_media_cost) / NULLIF(campaigns.total_billable_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0)), 0)',
      type: 'number',
      title: 'Actual Margin Percent Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.actual_margin_percent',
        short_description: 'Filter-only: per-row actual margin percent',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.planned_margin_value': {
      sql: 'campaigns.total_price_margin / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      title: 'Planned Margin Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.planned_margin',
        short_description: 'Filter-only: per-row planned margin',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.target_margin_value': {
      sql: 'campaigns.total_price_target_margin / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      title: 'Target Margin Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.target_margin',
        short_description: 'Filter-only: per-row target margin',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.margin_variance_value': {
      sql: '((campaigns.total_uncapped_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0)) - campaigns.total_media_cost) / NULLIF(campaigns.total_uncapped_revenue * (1 - COALESCE(campaigns.referral_commission_rate, 0)), 0) - campaigns.total_price_margin / NULLIF(campaigns.total_line_item_price, 0)) / NULLIF(campaigns.total_price_margin / NULLIF(campaigns.total_line_item_price, 0), 0)',
      type: 'number',
      title: 'Margin Variance Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.margin_variance',
        short_description: 'Filter-only: per-row margin variance',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.target_margin_variance_value': {
      sql: '(campaigns.total_price_target_margin / NULLIF(campaigns.total_line_item_price, 0) - campaigns.total_price_margin / NULLIF(campaigns.total_line_item_price, 0)) / NULLIF(campaigns.total_price_target_margin / NULLIF(campaigns.total_line_item_price, 0), 0)',
      type: 'number',
      title: 'Target Margin Variance Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.target_margin_variance',
        short_description: 'Filter-only: per-row target margin variance',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry,
    'campaigns.revenue_delivered_percent_value': {
      sql: 'campaigns.total_billable_revenue / NULLIF(campaigns.total_line_item_price, 0)',
      type: 'number',
      title: 'Revenue Delivered Percent Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'campaigns.revenue_delivered_percent',
        short_description: 'Filter-only: per-row revenue delivered percent',
        source_tables: ['metrics.campaigns']
      }
    } as const satisfies CubeDimensionEntry
  }
};
